import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  CircularProgress
} from '@material-ui/core';
import { useStyles } from './styles';
import { Map, TileLayer, Polygon, Marker, Popup, WMSTileLayer, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon issue
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png'
});

// Default farm boundary coordinates - farm outline in South Africa
const defaultFarmBoundary = [
  [-34.00256677544119, 19.491489831450753],
  [-34.00345620472669, 19.49157566213923],
  [-34.003349473704205, 19.492433969023995],
  [-34.00256677544119, 19.49230522299128],
  [-34.00256677544119, 19.491489831450753]
];

// Default center for South Africa if no boundary is provided
const defaultSouthAfricaCenter = [-29, 25]; // Default to South Africa center

// Calculate center of polygon for better map centering
function calculateCenter(coordinates) {
  try {
    // Handle both array of coordinates or GeoJSON boundary
    let points = coordinates;
    
    // If this is an array of coordinates
    if (Array.isArray(coordinates) && coordinates.length > 0) {
      var lats = [];
      var lngs = [];
      
      for (var i = 0; i < coordinates.length; i++) {
        if (Array.isArray(coordinates[i]) && coordinates[i].length >= 2) {
          lats.push(parseFloat(coordinates[i][0]));
          lngs.push(parseFloat(coordinates[i][1]));
        }
      }
      
      if (lats.length === 0 || lngs.length === 0) {
        return defaultSouthAfricaCenter;
      }
      
      var sumLat = 0;
      var sumLng = 0;
      
      for (var j = 0; j < lats.length; j++) {
        sumLat += lats[j];
      }
      
      for (var k = 0; k < lngs.length; k++) {
        sumLng += lngs[k];
      }
      
      var centerLat = sumLat / lats.length;
      var centerLng = sumLng / lngs.length;
      
      return [centerLat, centerLng];
    }
    
    // Default for invalid inputs
    return defaultSouthAfricaCenter;
  } catch (error) {
    console.error('Error calculating center:', error);
    return defaultSouthAfricaCenter;
  }
}

// Use default boundary for initial center calculation
const defaultCenter = calculateCenter(defaultFarmBoundary);

function LeafletMap(props) {
  // Extract props with defaults
  const center = props.center || defaultCenter;
  const zoom = props.zoom || 16;
  const height = props.height || 400;
  const title = props.title || "Farm Boundary Map";
  const markers = props.markers || [];
  const polygonPositions = props.polygonPositions || [];
  const wmsUrl = props.wmsUrl || null;
  const wmsLayers = props.wmsLayers || null;
  const wmsOptions = props.wmsOptions || {};
  
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  
  // Create a ref to access the Leaflet map instance
  const mapRef = useRef(null);

  const handleMapReady = () => {
    setIsLoading(false);
    console.log('Map is ready');
    
    // Fit map to polygon bounds when positions are available and valid
    if (mapRef.current && polygonPositions && polygonPositions.length >= 3) {
      try {
        console.log('Fitting map to bounds');
        const leafletMap = mapRef.current.leafletElement;
        leafletMap.fitBounds(polygonPositions);
      } catch (error) {
        console.error('Error fitting to bounds:', error);
      }
    }
  };
  
  // Update map bounds when polygonPositions change
  useEffect(() => {
    if (mapRef.current && polygonPositions && polygonPositions.length >= 3) {
      try {
        const leafletMap = mapRef.current.leafletElement;
        leafletMap.fitBounds(polygonPositions);
      } catch (error) {
        console.error('Error fitting to bounds on update:', error);
      }
    }
  }, [polygonPositions]);
  
  // Simple polygon styling options
  const polygonStyle = {
    color: '#4CAF50', // Green color
    weight: 3,        // Line thickness
    fillColor: '#A5D6A7', // Light green fill
    fillOpacity: 0.4, // Semi-transparent fill
    opacity: 0.8
  };

  return (
    <Card className={classes.card}>
      <CardHeader title={title} />
      <CardContent className={classes.content}>
        {isLoading ? (
          <div className={classes.loading}>
            <CircularProgress />
            <Typography variant="body1" style={{ marginTop: 16 }}>
              Loading map...
            </Typography>
          </div>
        ) : (
          <div className={classes.mapContainer} style={{ height: height + 'px' }}>
            <Map 
              center={center} 
              zoom={zoom} 
              style={{ height: '100%', width: '100%' }}
              onLayerAdd={handleMapReady}
              ref={mapRef}
            >
              {/* Base OpenStreetMap layer */}
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* Conditional WMS Layer */}
              {wmsUrl && wmsLayers && (
                <WMSTileLayer
                  url={wmsUrl}
                  layers={wmsLayers}
                  format={wmsOptions.format || "image/png"}
                  transparent={wmsOptions.transparent !== undefined ? wmsOptions.transparent : true}
                  version="1.1.1"
                  attribution={wmsOptions.attribution || "Data: © SANSA DESA"}
                  opacity={0.6}
                />
              )}
              
              {/* Render farm boundary polygon from direct coordinates */}
              {polygonPositions && polygonPositions.length >= 3 && (
                <Polygon 
                  positions={polygonPositions}
                  color="#4CAF50"  // Green color
                  weight={3}       // Line thickness
                  fillColor="#A5D6A7"  // Light green fill
                  fillOpacity={0.4}  // Semi-transparent fill
                />
              )}
              
              {/* Fallback to default boundary if no polygon positions provided */}
              {(!polygonPositions || polygonPositions.length < 3) && (
                <Polygon 
                  positions={defaultFarmBoundary}
                  color="#4CAF50"  // Green color
                  weight={3}         // Line thickness
                  fillColor="#A5D6A7"  // Light green fill
                  fillOpacity={0.4}  // Semi-transparent fill
                />
              )}
              
              {/* Render markers */}
              {markers && markers.map(function(marker, index) {
                var position = marker.position || [marker.lat, marker.lng];
                return (
                  <Marker 
                    key={index} 
                    position={position}
                  >
                    {marker.popupContent && (
                      <Popup>{marker.popupContent}</Popup>
                    )}
                  </Marker>
                );
              })}
            </Map>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default LeafletMap;
